import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Upload, Send } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QualificationForm = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    company: '',
    country: '',
    industry: '',
    contactName: '',
    email: '',
    phone: '',
    assetTypes: [],
    volumes: '',
    zones: [],
    painPoints: '',
    priority: '',
    existingInfra: '',
    budgetStage: '',
    additionalInfo: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Transform formData to match backend API
      const payload = {
        company: formData.company,
        country: formData.country,
        industry: formData.industry,
        contact_name: formData.contactName,
        email: formData.email,
        phone: formData.phone || null,
        asset_types: formData.assetTypes,
        volumes: formData.volumes || null,
        zones: formData.zones,
        pain_points: formData.painPoints || null,
        priority: formData.priority || null,
        existing_infra: formData.existingInfra || null,
        budget_stage: formData.budgetStage || null,
        additional_info: formData.additionalInfo || null,
      };

      const response = await axios.post(`${API}/qualification-form`, payload);
      
      if (response.status === 200) {
        toast({
          title: "Success!",
          description: "Thank you! We'll contact you within 24 hours.",
        });
        // Reset form
        setFormData({
          company: '',
          country: '',
          industry: '',
          contactName: '',
          email: '',
          phone: '',
          assetTypes: [],
          volumes: '',
          zones: [],
          painPoints: '',
          priority: '',
          existingInfra: '',
          budgetStage: '',
          additionalInfo: '',
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCheckboxChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(item => item !== value)
        : [...prev[field], value]
    }));
  };

  return (
    <section id="qualification-form" className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Get Your Deployment Plan
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Complete this comprehensive diagnostic to receive a tailored E2-Return implementation plan
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Form */}
          <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-200">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Company Info */}
              <div className="space-y-4">
                <h3 className="text-xl font-bold text-gray-900">Company Information</h3>
                
                <div>
                  <Label htmlFor="company">Company Name *</Label>
                  <Input 
                    id="company"
                    value={formData.company}
                    onChange={(e) => setFormData({...formData, company: e.target.value})}
                    required
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="country">Country *</Label>
                  <Input 
                    id="country"
                    value={formData.country}
                    onChange={(e) => setFormData({...formData, country: e.target.value})}
                    required
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="industry">Industry *</Label>
                  <Select value={formData.industry} onValueChange={(value) => setFormData({...formData, industry: value})}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="food">Food & Beverage</SelectItem>
                      <SelectItem value="automotive">Automotive</SelectItem>
                      <SelectItem value="pharma">Pharmaceuticals</SelectItem>
                      <SelectItem value="airport">Airports & Ground Handling</SelectItem>
                      <SelectItem value="retail">Retail & FMCG</SelectItem>
                      <SelectItem value="postal">Postal & Parcel</SelectItem>
                      <SelectItem value="coldchain">Cold Chain</SelectItem>
                      <SelectItem value="circular">Circular Economy</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Contact Info */}
              <div className="space-y-4">
                <h3 className="text-xl font-bold text-gray-900">Contact Details</h3>
                
                <div>
                  <Label htmlFor="contactName">Full Name *</Label>
                  <Input 
                    id="contactName"
                    value={formData.contactName}
                    onChange={(e) => setFormData({...formData, contactName: e.target.value})}
                    required
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input 
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input 
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Asset Details */}
              <div className="space-y-4">
                <h3 className="text-xl font-bold text-gray-900">Asset & Operations</h3>
                
                <div>
                  <Label>Type of Assets (select all that apply)</Label>
                  <div className="mt-2 space-y-2">
                    {['Plastic crates', 'Wooden pallets', 'Metal containers', 'Roll cages', 'RPAI', 'Airport equipment', 'Other'].map((asset) => (
                      <div key={asset} className="flex items-center gap-2">
                        <Checkbox 
                          id={asset}
                          checked={formData.assetTypes.includes(asset)}
                          onCheckedChange={() => handleCheckboxChange('assetTypes', asset)}
                        />
                        <Label htmlFor={asset} className="cursor-pointer font-normal">{asset}</Label>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <Label htmlFor="volumes">Estimated Asset Volumes</Label>
                  <Input 
                    id="volumes"
                    placeholder="e.g., 10,000 crates, 5,000 pallets"
                    value={formData.volumes}
                    onChange={(e) => setFormData({...formData, volumes: e.target.value})}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Zones of Use (select all that apply)</Label>
                  <div className="mt-2 space-y-2">
                    {['On-campus', 'Inter-campus', 'Off-campus', 'Cross-border'].map((zone) => (
                      <div key={zone} className="flex items-center gap-2">
                        <Checkbox 
                          id={zone}
                          checked={formData.zones.includes(zone)}
                          onCheckedChange={() => handleCheckboxChange('zones', zone)}
                        />
                        <Label htmlFor={zone} className="cursor-pointer font-normal">{zone}</Label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Pain Points & Priority */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="painPoints">Main Pain Points</Label>
                  <Textarea 
                    id="painPoints"
                    placeholder="Describe your current challenges with returnable packaging tracking..."
                    value={formData.painPoints}
                    onChange={(e) => setFormData({...formData, painPoints: e.target.value})}
                    rows={4}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="priority">Priority Level</Label>
                  <Select value={formData.priority} onValueChange={(value) => setFormData({...formData, priority: value})}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select priority" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="urgent">Urgent (need solution within 1 month)</SelectItem>
                      <SelectItem value="high">High (within 3 months)</SelectItem>
                      <SelectItem value="medium">Medium (within 6 months)</SelectItem>
                      <SelectItem value="low">Low (exploring options)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Technical & Budget */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="existingInfra">Existing Infrastructure</Label>
                  <Textarea 
                    id="existingInfra"
                    placeholder="Describe any existing tracking systems, WMS, ERP, etc."
                    value={formData.existingInfra}
                    onChange={(e) => setFormData({...formData, existingInfra: e.target.value})}
                    rows={3}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="budgetStage">Budget Stage</Label>
                  <Select value={formData.budgetStage} onValueChange={(value) => setFormData({...formData, budgetStage: value})}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select budget stage" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="approved">Budget Approved</SelectItem>
                      <SelectItem value="review">Under Review</SelectItem>
                      <SelectItem value="planning">Planning Phase</SelectItem>
                      <SelectItem value="exploring">Just Exploring</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="additionalInfo">Additional Information</Label>
                  <Textarea 
                    id="additionalInfo"
                    placeholder="Any other details you'd like to share..."
                    value={formData.additionalInfo}
                    onChange={(e) => setFormData({...formData, additionalInfo: e.target.value})}
                    rows={3}
                    className="mt-1"
                  />
                </div>
              </div>

              {/* File Upload Placeholder */}
              <div>
                <Label>Upload Documents (optional)</Label>
                <div className="mt-2 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-500 transition-colors cursor-pointer">
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">Click to upload or drag and drop</p>
                  <p className="text-xs text-gray-500 mt-1">PDF, XLSX, or Images (max 10MB)</p>
                </div>
              </div>

              {/* Submit Button */}
              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white py-6 text-lg font-semibold"
              >
                {isSubmitting ? 'Submitting...' : 'Get Your Deployment Plan'}
                <Send className="ml-2 w-5 h-5" />
              </Button>
            </form>
          </div>

          {/* Contextual Image & Info */}
          <div className="space-y-6">
            <img 
              src="https://images.unsplash.com/photo-1567789884554-0b844b597180"
              alt="Industrial facility"
              className="rounded-2xl shadow-2xl w-full"
            />
            
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4">What Happens Next?</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-xs font-bold">1</span>
                  </div>
                  <p className="text-gray-700">We review your requirements within 4 hours</p>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-xs font-bold">2</span>
                  </div>
                  <p className="text-gray-700">Receive a customized deployment plan and quote</p>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-xs font-bold">3</span>
                  </div>
                  <p className="text-gray-700">Schedule a demo or pilot deployment</p>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default QualificationForm;
